/**
 * The line under the types header that says what a compile does to the types: compiling, then each
 * type checked against the edit as the model answers, then what the last compile changed. A check
 * that wrote nothing says so until it is dismissed.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { TypeReconcileSummary } from '@/services/ai-function.service';
import type { TypeReconcileProgress } from '@/types/websocket.types';
import { PanelNote } from './panel-note';
import { reconcileLine } from './type-reconcile';

interface ReconcileStatusProps {
	hasTypes: boolean;
	lastReconcile: TypeReconcileSummary | null;
	reconciling: TypeReconcileProgress | null;
	reconcileFailedAt: string | null;
	protocolCompiling: boolean;
	className?: string;
}

const Running = ({ label, count, value, className }: { label: string; count?: string; value: number; className?: string }) => (
	<div role='status' className={cn('flex shrink-0 flex-col gap-1.5 px-4 py-1.5', className)}>
		<div className='flex items-center gap-2 text-xs leading-4 text-gray-800'>
			<Loader2 className='size-3.5 shrink-0 animate-spin' aria-hidden />
			<p className='min-w-0 flex-1 truncate'>{label}</p>
			{count && <span className='shrink-0 tabular-nums'>{count}</span>}
		</div>
		<Progress
			value={value}
			aria-label={label}
			className='h-1 bg-gray-200 **:data-[slot=progress-indicator]:bg-gray-900'
		/>
	</div>
);

export const ReconcileStatus = ({
	hasTypes,
	lastReconcile,
	reconciling,
	reconcileFailedAt,
	protocolCompiling,
	className
}: ReconcileStatusProps) => {
	const [dismissedFailure, setDismissedFailure] = useState<string | null>(null);

	if (reconciling) {
		const { checked, total, stage } = reconciling;
		return stage === 'writing' ? (
			<Running label='Writing types for the new cases…' value={100} className={className} />
		) : (
			<Running
				label='Checking the types against the edit'
				count={`${checked.length} of ${total}`}
				value={total ? (checked.length / total) * 100 : 0}
				className={className}
			/>
		);
	}
	if (protocolCompiling && hasTypes) {
		return (
			<p role='status' className={cn('flex shrink-0 items-center gap-2 px-4 py-1.5 text-xs leading-4 text-gray-800', className)}>
				<Loader2 className='size-3.5 shrink-0 animate-spin' aria-hidden />
				Compiling the protocol. The types get checked against it once it lands.
			</p>
		);
	}
	return (
		<>
			{reconcileFailedAt && dismissedFailure !== reconcileFailedAt && (
				<PanelNote role='alert' onDismiss={() => setDismissedFailure(reconcileFailedAt)} className={cn('px-4 py-1.5', className)}>
					The last compile&apos;s check of the types didn&apos;t finish, so they were left as they were. To check
					them again, edit the protocol and Save &amp; compile.
				</PanelNote>
			)}
			{lastReconcile && (
				<p
					className={cn('shrink-0 px-4 py-1.5 text-xs leading-4 text-gray-800', className)}
					title={
						lastReconcile.removed.length
							? `Removed: ${lastReconcile.removed.map((t) => t.description).join('; ')}`
							: undefined
					}
				>
					Last compile: {reconcileLine(lastReconcile)}
				</p>
			)}
		</>
	);
};
